import { Button, Pagination, Table } from '@/components/commons';
import { APP_ROUTER } from '@/routes/routes';
import { AyumiService } from '@/services/ayumi';
import { ENDPOINT } from '@/services/endpoint';
import {
  AYUMI_TOPIC_STATUS_LABELS,
  AyumiTopicStatus,
} from '@/types/common/ayumi';
import { IGetListAyumiResponse } from '@/types/response';
import { convertRequest } from '@/utils';
import { PlusCircleFilled } from '@ant-design/icons';
import { Select, TableColumnsType } from 'antd';
import moment from 'moment';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { IntroVideoItemsSection } from './IntroVideoItemsSection';
import { IntroVideoSection } from './IntroVideoSection';

interface IProps {
  title?: string;
}

interface IParams {
  limit: number;
  page: number;
  status?: AyumiTopicStatus | '';
  sortBy: string;
  sortDirection: 'ASC' | 'DESC';
}

const formatDate = (value?: string | null) =>
  value ? moment(value).format('YYYY.MM.DD') : '―';

export const AyumiList: React.FC<IProps> = ({ title }) => {
  const navigate = useNavigate();
  // サーバ既定は createdAt DESC（作成順）。一覧は課題の定義順に揃える
  const [params, setParams] = useState<IParams>({
    limit: 10,
    page: 1,
    sortBy: 'sortOrder',
    sortDirection: 'ASC',
  });

  const { data, isFetching } = useQuery(
    [ENDPOINT.AYUMI.GET_LIST, params],
    () => AyumiService.getList(convertRequest(params)),
  );

  const columns: TableColumnsType = [
    {
      title: '課題名',
      dataIndex: 'name',
      width: '300px',
    },
    {
      title: '公開日',
      dataIndex: 'publicAt',
      width: '120px',
      render: (value) => formatDate(value),
    },
    {
      title: '公開終了日',
      dataIndex: 'publicEndAt',
      width: '140px',
      render: (value) => formatDate(value),
    },
    {
      title: 'ステータス',
      dataIndex: 'status',
      width: '120px',
      render: (value: AyumiTopicStatus) =>
        AYUMI_TOPIC_STATUS_LABELS[value] ?? '―',
    },
    {
      title: '操作',
      align: 'center',
      width: '160px',
      render: (record: IGetListAyumiResponse) => (
        <Button
          type='primary'
          onClick={() =>
            navigate(APP_ROUTER.AYUMI.UPDATE.replace(':id', String(record.id)))
          }
        >
          編集
        </Button>
      ),
    },
  ];

  return (
    <div className='w-full'>
      <Helmet>
        <title>{title}</title>
      </Helmet>

      <IntroVideoSection />
      <IntroVideoItemsSection />

      <header className='flex items-center justify-between mb-4'>
        <h3 className='font-bold text-[20px]'>課題管理｜コンテンツ一覧</h3>
        <div className='flex items-center gap-2'>
          <Select
            allowClear
            className='w-[160px]'
            placeholder='ステータス'
            value={params.status === '' ? undefined : params.status}
            onChange={(value) =>
              setParams((prev) => ({
                ...prev,
                // 未選択は空文字にせず undefined を渡す（空文字だと下書きに絞られてしまう）
                status: value ?? undefined,
                page: 1,
              }))
            }
            options={[
              { value: AyumiTopicStatus.PUBLIC, label: '公開中' },
              { value: AyumiTopicStatus.DRAFT, label: '下書き' },
              { value: AyumiTopicStatus.HIDDEN, label: '非表示' },
            ]}
          />
          <Button
            type='primary'
            icon={<PlusCircleFilled />}
            onClick={() => navigate(APP_ROUTER.AYUMI.CREATE)}
          >
            新規作成
          </Button>
        </div>
      </header>

      <Table
        rowKey='id'
        columns={columns}
        dataSource={data?.data?.data ?? []}
        loading={isFetching}
        pagination={false}
      />

      <Pagination
        className='mt-4'
        current={params.page}
        pageSize={params.limit}
        total={data?.data?.pagination?.total ?? 0}
        onChange={(page, pageSize) =>
          setParams((prev) => ({ ...prev, page, limit: pageSize }))
        }
      />
    </div>
  );
};
